/**
 * What the two browsers say to each other, and what they refuse to believe.
 *
 * Everything here arrives over a network connection. It is *supposed* to come from another
 * session of the same account, and the server will only introduce sessions that belong
 * together — but "supposed to" is not a property the receiving code can check, and the
 * cost of being wrong is a bad record written into somebody's library or a claimed file
 * size that makes the tab allocate until it dies. So every field is checked rather than
 * cast, and the checks live here where they can be tested without a connection.
 */

import { isModelHash } from '../domain/hash.js';
import { normaliseColour } from '../domain/colour.js';
import { normaliseRoomScale } from '../domain/nudge.js';
import type {
  Amendment,
  ManifestEntry,
  Manifest,
  ModelHash,
  ModelPresentation,
  Tombstone,
} from '../domain/sync.js';

/** Longer than any sensible file name, short enough not to be a way to spend storage. */
export const MAX_NAME_LENGTH = 200;

/**
 * The largest file that will be accepted.
 *
 * Not a judgement about STLs so much as a bound on what a single claimed transfer can make
 * this tab allocate, since the buffer is sized from what the sender says before any of it
 * has arrived.
 */
export const MAX_FILE_BYTES = 512 * 1024 * 1024;

/** The largest library a peer may describe in one go. */
const MAX_MANIFEST_ENTRIES = 10_000;

export type Control =
  | { readonly t: 'manifest?' }
  | { readonly t: 'manifest'; readonly manifest: Manifest }
  | { readonly t: 'pull'; readonly hashes: readonly ModelHash[] }
  | { readonly t: 'file'; readonly entry: ManifestEntry }
  | { readonly t: 'end'; readonly hash: ModelHash }
  | { readonly t: 'missing'; readonly hash: ModelHash }
  | { readonly t: 'tombstones'; readonly tombstones: readonly Tombstone[] }
  | { readonly t: 'amend'; readonly amendments: readonly Amendment[] }
  | { readonly t: 'done' };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown, key: string): string | undefined {
  const field = isRecord(value) ? value[key] : undefined;
  return typeof field === 'string' ? field : undefined;
}

/** A finite, non-negative whole number, which is what every count and time here is. */
function readCount(value: unknown, key: string): number | undefined {
  const field = isRecord(value) ? value[key] : undefined;
  if (typeof field !== 'number' || !Number.isFinite(field) || field < 0) return undefined;
  return Math.floor(field);
}

/** Any number at all, left to whoever asked for it to say what it may be. */
function readNumber(value: unknown, key: string): number | undefined {
  const field = isRecord(value) ? value[key] : undefined;
  return typeof field === 'number' ? field : undefined;
}

function readHash(value: unknown, key: string): ModelHash | undefined {
  const field = isRecord(value) ? value[key] : undefined;
  return isModelHash(field) ? field : undefined;
}

export function readManifestEntry(value: unknown): ManifestEntry | undefined {
  const hash = readHash(value, 'hash');
  const name = readString(value, 'name');
  const byteSize = readCount(value, 'byteSize');
  const addedAt = readCount(value, 'addedAt');

  if (hash === undefined || name === undefined) return undefined;
  if (byteSize === undefined || byteSize > MAX_FILE_BYTES) return undefined;
  if (addedAt === undefined) return undefined;

  return {
    hash,
    name: name.slice(0, MAX_NAME_LENGTH),
    byteSize,
    addedAt,
    ...readPresentation(value),
  };
}

/**
 * Read a change to how a model is shown.
 *
 * The hash is the only part that has to be there. An amendment carrying nothing this
 * version understands is not an error — a newer session may be reconciling something this
 * one has never heard of — but it is also nothing to act on, so it is dropped.
 */
export function readAmendment(value: unknown): Amendment | undefined {
  const hash = readHash(value, 'hash');
  if (hash === undefined) return undefined;

  const presentation = readPresentation(value);
  if (Object.keys(presentation).length === 0) return undefined;

  return { hash, ...presentation };
}

/**
 * Read the decisions about how to show a model, dropping any that do not check out.
 *
 * A value we could not store is dropped rather than refusing the whole message. The file
 * is the point; how it is shown is a preference, and losing one costs nothing next to
 * refusing a model or abandoning a share over it.
 *
 * A decision's time is dropped with the decision it belongs to, so the two can never come
 * apart — a time with no value would claim recency for nothing, and a value with no time
 * is exactly what a session that predates this sends, which the merge already handles.
 */
function readPresentation(value: unknown): ModelPresentation {
  const colour = normaliseColour(readString(value, 'colour') ?? '');
  const roomScale = normaliseRoomScale(readNumber(value, 'roomScale'));
  const colourAt = readCount(value, 'colourAt');
  const roomScaleAt = readCount(value, 'roomScaleAt');

  return {
    ...(colour === undefined ? {} : { colour }),
    ...(colour === undefined || colourAt === undefined ? {} : { colourAt }),
    ...(roomScale === undefined ? {} : { roomScale }),
    ...(roomScale === undefined || roomScaleAt === undefined ? {} : { roomScaleAt }),
  };
}

export function readTombstone(value: unknown): Tombstone | undefined {
  const hash = readHash(value, 'hash');
  const deletedAt = readCount(value, 'deletedAt');
  if (hash === undefined || deletedAt === undefined) return undefined;
  return { hash, deletedAt };
}

/** Read a list, dropping entries that do not check out rather than failing the whole. */
function readList<T>(value: unknown, read: (item: unknown) => T | undefined): T[] {
  if (!Array.isArray(value)) return [];
  const items: T[] = [];
  for (const item of value.slice(0, MAX_MANIFEST_ENTRIES)) {
    const parsed = read(item);
    if (parsed !== undefined) items.push(parsed);
  }
  return items;
}

/** Turn a received control frame into something usable, or `undefined` if it is not. */
export function parseControl(text: string): Control | undefined {
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return undefined;
  }

  const kind = readString(body, 't');
  if (kind === undefined || !isRecord(body)) return undefined;

  switch (kind) {
    case 'manifest?':
      return { t: 'manifest?' };

    case 'manifest':
      return {
        t: 'manifest',
        manifest: {
          models: readList(body.models, readManifestEntry),
          tombstones: readList(body.tombstones, readTombstone),
        },
      };

    case 'pull':
      return {
        t: 'pull',
        hashes: readList(body.hashes, (item) => (isModelHash(item) ? item : undefined)),
      };

    case 'file': {
      const entry = readManifestEntry(body.entry);
      return entry === undefined ? undefined : { t: 'file', entry };
    }

    case 'end':
    case 'missing': {
      const hash = readHash(body, 'hash');
      return hash === undefined ? undefined : { t: kind, hash };
    }

    case 'tombstones':
      return {
        t: 'tombstones',
        tombstones: readList(body.tombstones, readTombstone),
      };

    case 'amend':
      return {
        t: 'amend',
        amendments: readList(body.amendments, readAmendment),
      };

    case 'done':
      return { t: 'done' };

    default:
      return undefined;
  }
}

/** The wire form of a control frame. */
export function encode(control: Control): string {
  switch (control.t) {
    case 'manifest':
      return JSON.stringify({
        t: 'manifest',
        models: control.manifest.models,
        tombstones: control.manifest.tombstones,
      });
    case 'pull':
      return JSON.stringify({ t: 'pull', hashes: control.hashes });
    case 'file':
      return JSON.stringify({ t: 'file', entry: control.entry });
    case 'end':
    case 'missing':
      return JSON.stringify({ t: control.t, hash: control.hash });
    case 'tombstones':
      return JSON.stringify({ t: 'tombstones', tombstones: control.tombstones });
    case 'amend':
      return JSON.stringify({ t: 'amend', amendments: control.amendments });
    case 'manifest?':
    case 'done':
      return JSON.stringify({ t: control.t });
  }
}
